import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel } from './FieldLabel';
import { FileDropzone } from './FileDropzone';
import { FormActions, GroupField, ValidationBanner, type CodeFormProps } from './qrCodeFormParts';

/** A code that opens a file uploaded here, so a document needs no web page of its own. */
export function FileCodeForm({ groups, qrCode, submitting = false, onSubmit, onCancel, kindSwitch }: CodeFormProps) {
  const { t } = useTranslation();
  const editableGroups = groups.filter((group) => group.canEditQrCodes);

  const [ownerGroupId, setOwnerGroupId] = useState(qrCode?.ownerGroupId ?? editableGroups[0]?.id ?? '');
  const [title, setTitle] = useState(qrCode?.title ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [slug, setSlug] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!ownerGroupId) {
      setValidationError(t('qrCode.validation.groupRequired'));
      return;
    }

    if (!title.trim()) {
      setValidationError(t('qrCode.validation.titleRequired'));
      return;
    }

    if (!file) {
      setValidationError(t('qrCode.file.required'));
      return;
    }

    setValidationError(null);
    await onSubmit({
      ownerGroupId,
      title: title.trim(),
      kind: 'File',
      file,
      slug: slug.trim() === '' ? undefined : slug.trim(),
      isActive: true,
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-label={t('qrCode.formLabelCreate')}>
      <ValidationBanner message={validationError} />

      <GroupField
        groups={editableGroups}
        value={ownerGroupId}
        disabled={submitting}
        onChange={setOwnerGroupId}
        kindSwitch={kindSwitch}
      />

      <div className="field">
        <FieldLabel htmlFor="qr-title" icon="pi-tag" required>
          {t('qrCode.title')}
        </FieldLabel>
        <input
          id="qr-title"
          type="text"
          className="form-control"
          aria-required="true"
          value={title}
          disabled={submitting}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className="field">
        <FieldLabel htmlFor="qr-file" icon="pi-file" required>
          {t('qrCode.file.label')}
        </FieldLabel>
        <FileDropzone id="qr-file" file={file} disabled={submitting} required onChange={setFile} />
      </div>

      <div className="field">
        <FieldLabel htmlFor="qr-slug" icon="pi-hashtag">
          {t('qrCode.linkIdCustom')}
        </FieldLabel>
        <input
          id="qr-slug"
          type="text"
          className="form-control"
          value={slug}
          placeholder={t('qrCode.linkIdPlaceholder')}
          disabled={submitting}
          onChange={(event) => setSlug(event.target.value)}
        />
        <span className="hint">
          {t('qrCode.linkIdHint')} {t('qrCode.linkIdLockedHint')}
        </span>
      </div>

      <FormActions submitting={submitting} isEditing={false} onCancel={onCancel} />
    </form>
  );
}
